function formatNumber(value, maxFractionDigits = 1) {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return '-';
    return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFractionDigits
    }).format(numericValue);
}

function formatShoppingDisplayAmount(amount, unit) {
    const numericAmount = Number(amount || 0);
    if (!Number.isFinite(numericAmount)) return '-';
    const normalizedUnit = String(unit || '').trim().toLowerCase();

    if (['g', 'gr', 'gram'].includes(normalizedUnit)) {
        if (numericAmount >= 1000) {
            return `${formatNumber(numericAmount / 1000)} kg`;
        }
        return `${formatNumber(numericAmount, 0)} g`;
    }

    if (normalizedUnit === 'kg') {
        return `${formatNumber(numericAmount)} kg`;
    }

    if (normalizedUnit === 'ml') {
        if (numericAmount >= 1000) {
            return `${formatNumber(numericAmount / 1000)} lt`;
        }
        return `${formatNumber(numericAmount, 0)} ml`;
    }

    if (['l', 'lt', 'litre'].includes(normalizedUnit)) {
        return `${formatNumber(numericAmount)} lt`;
    }

    if (['adet', 'tane', 'paket', 'bağ', 'bag', 'demet', 'diş', 'dis', 'dal', 'file'].includes(normalizedUnit)) {
        return `${formatNumber(numericAmount, 0)} ${unit || 'adet'}`.trim();
    }

    return `${formatNumber(numericAmount)} ${unit || ''}`.trim();
}

function resolveShoppingDisplayAmountMeta(item = {}) {
    const suggestedDisplay = item?.suggestedPurchase?.display;
    if (suggestedDisplay) {
        return {
            display: suggestedDisplay,
            source: 'suggestedPurchase.display'
        };
    }

    const suggestedAmount = Number(item?.suggestedAmount);
    const suggestedUnit = item?.unit || item?.normalizedUnit || '';
    if (Number.isFinite(suggestedAmount) && suggestedAmount > 0) {
        return {
            display: formatShoppingDisplayAmount(suggestedAmount, suggestedUnit),
            source: 'suggestedAmount'
        };
    }

    const normalizedAmount = Number(item?.normalizedAmount ?? item?.amount ?? 0);
    const normalizedUnit = item?.normalizedUnit || item?.unit || '';
    return {
        display: formatShoppingDisplayAmount(normalizedAmount, normalizedUnit),
        source: 'normalizedAmount'
    };
}

function resolveMarketDisplayAmountMeta(item = {}) {
    const marketAmount = Number(
        item?.marketAmount ??
        item?.canonicalContract?.marketAmount ??
        item?.suggestedAmount ??
        item?.amount ??
        0
    );
    const marketUnit = String(
        item?.marketUnit ||
        item?.canonicalContract?.marketUnit ||
        item?.unit ||
        ''
    ).trim();

    if (Number.isFinite(marketAmount) && marketAmount > 0) {
        return {
            display: formatShoppingDisplayAmount(marketAmount, marketUnit),
            source: 'marketAmount'
        };
    }

    return resolveShoppingDisplayAmountMeta(item);
}

function resolveCanonicalDisplayAmountMeta(item = {}) {
    const canonicalAmount = Number(
        item?.canonicalAmount ??
        item?.canonicalContract?.canonicalAmount ??
        item?.normalizedAmount ??
        item?.suggestedAmount ??
        item?.amount ??
        0
    );
    const canonicalUnit = String(
        item?.canonicalUnit ||
        item?.canonicalContract?.canonicalUnit ||
        item?.normalizedUnit ||
        item?.unit ||
        ''
    ).trim();

    return {
        display: formatShoppingDisplayAmount(canonicalAmount, canonicalUnit),
        source: item?.canonicalAmount !== undefined || item?.canonicalContract?.canonicalAmount !== undefined
            ? 'canonicalAmount'
            : 'normalizedAmount'
    };
}

function resolveNormalizationExplainabilityMeta(item = {}) {
    const marketMeta = resolveMarketDisplayAmountMeta(item);
    const canonicalMeta = resolveCanonicalDisplayAmountMeta(item);
    const measurementType = String(
        item?.measurementType ||
        item?.canonicalContract?.measurementType ||
        ''
    ).trim();
    const conversionConfidence = String(
        item?.conversionConfidence ||
        item?.canonicalContract?.conversionConfidence ||
        ''
    ).trim();
    const resolutionStatus = String(
        item?.resolutionStatus ||
        item?.canonicalContract?.resolutionStatus ||
        ''
    ).trim();
    const snapshot = item?.canonicalSourceSnapshot || item?.canonicalContract?.canonicalSourceSnapshot || null;
    const showCanonicalDetail = Boolean(
        marketMeta.display &&
        canonicalMeta.display &&
        marketMeta.display !== canonicalMeta.display
    );

    return {
        marketDisplay: marketMeta.display,
        canonicalDisplay: canonicalMeta.display,
        measurementType,
        conversionConfidence,
        resolutionStatus,
        snapshot,
        showCanonicalDetail
    };
}

function buildShoppingDisplayLine(item = {}) {
    const baseName = item?.ingredientName || item?.name || '';
    const amountMeta = resolveShoppingDisplayAmountMeta(item);
    return `${amountMeta.display} ${baseName}`.trim();
}

export {
    formatShoppingDisplayAmount,
    resolveShoppingDisplayAmountMeta,
    resolveMarketDisplayAmountMeta,
    resolveCanonicalDisplayAmountMeta,
    resolveNormalizationExplainabilityMeta,
    buildShoppingDisplayLine
};
